import React from 'react';

import { logButtonClick } from 'features/navigation/utils/analytics';

import { IPickerAspect } from '@rbi-ctg/menu';
import { getModifierType } from 'components/product-detail-v2/modifier/utils';
import { MenuObjectTypes } from 'enums/menu';
import { ClickEventComponentNames } from 'state/amplitude';
import { selectors, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { useMenuContext } from 'state/menu';
import { useProductWizardContext } from 'state/product-wizard-v2';
import useProductWizardFlow from 'state/product-wizard-v2/hooks/use-product-wizard-flow';
import { ModifierTypes, UserSelectionPickerAspect } from 'state/product-wizard-v2/types';
import { getSingleOrMultipleModifierSelectionsDefaults } from 'state/product-wizard-v2/utils';
import { pickerAspectOptHasResolution } from 'utils/menu';
import { buildCalorieMapForPickerPossibilities } from 'utils/menu/picker-calorie-ranges';

import { PickerAspectsUpdateDialog } from '../dialogs/picker-aspects-update-dialog';
import { DEFAULT_SLOT_KEY } from '../product-item-flow/constants';
import { logCTAEvent } from '../utils';

import { FIRST_INDEX } from './constants';
import { PickerAspectGroup } from './picker-aspect-group';
import { IHandlePickerChangeParams, IPickerAspectSelectionArgs, IPickerAspects } from './types';
import {
  findCheapestPickerOption,
  findMatchingPickerOption,
  getPickerOptionPriceFromSelections,
} from './utils';

export const ProductPickerFlow = React.memo<IPickerAspects>(
  ({
    pickerSlotNumberToCustomize,
    pickerData,
    filteredPickerAspects,
    selectedPickerAspects,
    setSelectedPickerAspects,
    getPreviousPickerSelectionIdentifiers,
  }) => {
    const { pricingFunction } = useMenuContext();

    const { isOffer } = useMenuContext();
    const selectedOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);

    const {
      updateSelectionStateWithId,
      updatePickerAspectSelections,
      pickerFlowHasUpdates,
      resetPickerFlowUpdates,
      userSelections,
      selectedProduct,
      updateSingleOrMultipleModifierSelection,
      setSingleOrMultipleModifierSelection,
    } = useProductWizardContext();

    const { rewardBenefitId } = useProductWizardFlow();

    const isMounted = React.useRef<boolean>(false);

    const [
      confirmationDialogParams,
      handlePickerAspectsUpdateDialog,
    ] = React.useState<IHandlePickerChangeParams | null>(null);

    const isFirstPickerAspect = pickerSlotNumberToCustomize
      ? pickerData.pickerAspects.findIndex(aspect => aspect._id === pickerSlotNumberToCustomize) ===
        FIRST_INDEX
      : true;

    React.useEffect(() => {
      if (!isMounted.current) {
        // Skip the effect logic on the first render
        isMounted.current = true;
        return;
      }

      // If applicable, creates a new set of modifiers selections based on the new selectedProduct ( for both item and combo ).
      if (selectedProduct._type === MenuObjectTypes.COMBO) {
        const modifiersSelections = getSingleOrMultipleModifierSelectionsDefaults(
          userSelections.comboSlot
        );
        setSingleOrMultipleModifierSelection(modifiersSelections);
      } else {
        const modifiers = selectedProduct.options || [];
        const foundSingleOrMultipleChoice = modifiers.find(modifier => {
          const modifierType = getModifierType(modifier);
          return [ModifierTypes.MULTIPLE_CHOICE, ModifierTypes.SINGLE_CHOICE].includes(
            modifierType
          );
        });

        if (foundSingleOrMultipleChoice) {
          updateSingleOrMultipleModifierSelection(DEFAULT_SLOT_KEY, {
            key: foundSingleOrMultipleChoice._key,
            name: foundSingleOrMultipleChoice.name.locale,
            maxAmount: foundSingleOrMultipleChoice.maxAmount,
            type: getModifierType(foundSingleOrMultipleChoice),
            isPristine: true,
            isSaved: false,
          });
        } else {
          setSingleOrMultipleModifierSelection(null);
        }
      }
    }, [
      selectedProduct,
      setSingleOrMultipleModifierSelection,
      updateSingleOrMultipleModifierSelection,
      userSelections.comboSlot,
    ]);

    const calorieMap = React.useMemo(() => buildCalorieMapForPickerPossibilities(pickerData), [
      pickerData,
    ]);

    const handleOnAspectOptionClick = React.useCallback(
      (args: IPickerAspectSelectionArgs) => {
        let newSelectedPickerAspects: UserSelectionPickerAspect;
        const pickerAspectsLength = pickerData.pickerAspects.length;
        // Check new resolved options for the preceding aspects unless it's the last one
        if (pickerAspectsLength > 1 && args.index !== pickerAspectsLength - 1) {
          const pickerSelections = {} as UserSelectionPickerAspect;
          pickerData.pickerAspects.slice(args.index + 1, pickerAspectsLength).forEach(aspect => {
            const possibleOptsForPickerAspect = aspect.pickerAspectOptions.filter(option =>
              pickerAspectOptHasResolution(
                option.identifier,
                [...getPreviousPickerSelectionIdentifiers(0, args.index), args.optionIdentifier],
                pickerData.options
              )
            );
            const currentMatchingSelectedOption = possibleOptsForPickerAspect.find(
              opt => opt?.identifier === selectedPickerAspects?.[aspect._id]
            );
            // Use the value in state if it's already a possible resolved option
            // Else, choose the first option in the list to default select
            pickerSelections[aspect._id] = currentMatchingSelectedOption
              ? currentMatchingSelectedOption.identifier
              : possibleOptsForPickerAspect[0].identifier;
          });
          newSelectedPickerAspects = {
            ...selectedPickerAspects,
            ...pickerSelections,
            [args.aspect._id]: args.optionIdentifier,
          };
        } else {
          newSelectedPickerAspects = {
            ...selectedPickerAspects,
            [args.aspect._id]: args.optionIdentifier,
          };
        }

        logButtonClick({
          attributes: {
            component: ClickEventComponentNames.PRODUCT_SELECTION_MODIFICATION,
            componentId: args.aspect?._id,
            headerText: args.aspect?.name?.locale,
            name: args.optionIdentifier,
          },
        });

        setSelectedPickerAspects(newSelectedPickerAspects);
        updatePickerAspectSelections(newSelectedPickerAspects);

        updateSelectionStateWithId({
          id: args.aspect._id,
          type: MenuObjectTypes.PICKER_ASPECT,
          isDirty: true,
        });
      },
      [
        getPreviousPickerSelectionIdentifiers,
        pickerData.options,
        pickerData.pickerAspects,
        selectedPickerAspects,
        setSelectedPickerAspects,
        updatePickerAspectSelections,
        updateSelectionStateWithId,
      ]
    );

    const handlePickerAspectChange = React.useCallback(
      (params: IHandlePickerChangeParams) => {
        if (pickerFlowHasUpdates) {
          handlePickerAspectsUpdateDialog(params);
        } else {
          handleOnAspectOptionClick(params.args);
        }

        logButtonClick({
          attributes: {
            component: ClickEventComponentNames.PRODUCT_SELECTION_MODIFICATION,
            componentId: params.args.aspect._id,
            headerText: params.args.aspect.name.locale,
            name: params.args.optionIdentifier,
          },
        });
      },
      [handleOnAspectOptionClick, pickerFlowHasUpdates]
    );

    const renderPickerAspectGroup = ({
      aspect,
      aspectIndex,
    }: {
      aspect: IPickerAspect;
      aspectIndex: number;
    }) => (
      <PickerAspectGroup
        aspectIndex={aspectIndex}
        calorieMap={calorieMap}
        handlePickerAspectChange={handlePickerAspectChange}
        key={aspect._id}
        pickerAspect={aspect}
        userSelectedPickerAspects={selectedPickerAspects}
        imageForPickerAspectOption={imageForPickerAspectOption}
        priceForPickerAspectOption={priceForPickerAspectOption}
      />
    );

    // We only show the first picker aspect on the ProductHome page. Every subsequent picker aspect
    // should be shown in a modal.
    const [firstPickerAspect, currentPickerAspect] = [
      filteredPickerAspects[FIRST_INDEX],
      filteredPickerAspects.find(aspect => aspect._id === pickerSlotNumberToCustomize),
    ];

    /**
     * Returns the image of the picker aspect option that matches the given identifier.
     *
     * @param pickerAspectOptionIdentifier - The identifier of the picker aspect option, for example 'nothanks' or 'baconandcheese'
     * @returns The image of the picker aspect option that matches the given identifier.
     */
    const imageForPickerAspectOption = (pickerAspectOptionIdentifier: string) => {
      const matchingOption = findMatchingPickerOption({
        pickerData,
        pickerAspectOptionIdentifier,
        selectedPickerAspects,
        currentAspectId: pickerSlotNumberToCustomize,
      });

      return matchingOption?.option?.image;
    };

    /**
     * Returns the price of the picker aspect option that matches the given identifier. Pricing for the picker
     * aspects always assumes the cheapest option for every subsequent aspect. For example, if you're browsing
     * the combo size picker aspect and the next picker aspect is the bacon and cheese option, the price will
     * be the cheapest bacon and cheese option, i.e., "no thanks".
     */
    const priceForPickerAspectOption = (pickerAspectOptionIdentifier: string) => {
      const pricingParams = {
        pickerData,
        pickerAspectOptionIdentifier,
        pricingFunction,
      };

      if (rewardBenefitId) {
        return 0;
      }

      if (isOffer && selectedOffer) {
        return selectedOffer.offerPrice || 0;
      }

      // For first aspect, use cheapest subsequent options
      if (isFirstPickerAspect) {
        return findCheapestPickerOption(pricingParams) || 0;
      }

      // For subsequent aspects, use current user selections
      return getPickerOptionPriceFromSelections({
        ...pricingParams,
        currentPickerAspectId: pickerSlotNumberToCustomize,
        productWizardFlowUserSelections: userSelections.pickerAspects,
      });
    };

    const confirmLabel = t('reset');
    const cancelLabel = t('discard');

    return (
      <>
        {renderPickerAspectGroup({
          aspect: currentPickerAspect || firstPickerAspect,
          aspectIndex: currentPickerAspect
            ? filteredPickerAspects.findIndex(aspect => aspect._id === pickerSlotNumberToCustomize)
            : FIRST_INDEX,
        })}

        {/* Reset Confirmation Dialog */}
        <PickerAspectsUpdateDialog
          showDialog={!!confirmationDialogParams}
          confirmLabel={confirmLabel}
          cancelLabel={cancelLabel}
          onConfirm={() => {
            if (confirmationDialogParams?.args) {
              logCTAEvent(confirmLabel);
              handleOnAspectOptionClick(confirmationDialogParams.args);
              // We should reset user selection updates after a picker aspect is changed.
              resetPickerFlowUpdates();
            }
            handlePickerAspectsUpdateDialog(null);
          }}
          onCancel={() => {
            logCTAEvent(cancelLabel);
            confirmationDialogParams?.onCancel();
            handlePickerAspectsUpdateDialog(null);
          }}
        />
      </>
    );
  }
);
